import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Add as AddIcon, 
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Church as ChurchIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface GestiuneParohie {
  id: string;
  parohieId: string;
  nume: string;
  parohie: string;
  oras: string;
  provincia: string;
  regione: string;
  contBancar: string;
  banca: string;
  soldInitial: number;
  soldCurent: number;
  responsabilGestiune: string;
  responsabilId: string;
  status: 'activ' | 'inactiv' | 'suspendat';
  ultimaTranzactie: string;
  tranzactiiLuna: number;
  venituriLuna: number;
  cheltuieliLuna: number;
  tip: 'parohie' | 'episcopie';
}

interface Produs {
  id: string;
  nume: string;
  cantitate: number;
  unitate: string;
  pretUnitar: number;
  valoareTotala: number;
  categorie: string;
}

interface BonTransfer {
  id: string;
  numar: string;
  data: string;
  deLa: string;
  catre: string;
  produse: Produs[];
  totalValoare: number;
  status: 'in_asteptare' | 'confirmat' | 'anulat';
  observatii?: string;
}

interface FacturaIntrare {
  id: string;
  numar: string;
  data: string;
  furnizor: string;
  produse: Produs[];
  totalValoare: number;
  status: 'neplatita' | 'platita' | 'anulata';
  observatii?: string;
}

const Gestiuni: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegione, setFilterRegione] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedGestiune, setSelectedGestiune] = useState<GestiuneParohie | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const gestiuni: GestiuneParohie[] = [
    // Gestiuni Episcopie
    {
      id: 'episcopie_depozit',
      parohieId: 'episcopie',
      nume: 'Depozit Episcopie',
      parohie: 'Episcopia Ortodoxă Română din Italia',
      oras: 'Roma',
      provincia: 'Roma',
      regione: 'Lazio',
      contBancar: 'IT89M0300203280100000009999',
      banca: 'Intesa Sanpaolo',
      soldInitial: 50000,
      soldCurent: 75000,
      responsabilGestiune: 'Părintele Episcop',
      responsabilId: 'episcop1',
      status: 'activ',
      ultimaTranzactie: '2024-01-15',
      tranzactiiLuna: 45,
      venituriLuna: 15000,
      cheltuieliLuna: 8000,
      tip: 'episcopie'
    },
    {
      id: 'episcopie_productie',
      parohieId: 'episcopie',
      nume: 'Producție Episcopie',
      parohie: 'Episcopia Ortodoxă Română din Italia',
      oras: 'Roma',
      provincia: 'Roma',
      regione: 'Lazio',
      contBancar: 'IT89M0300203280100000009999',
      banca: 'Intesa Sanpaolo',
      soldInitial: 25000,
      soldCurent: 18000,
      responsabilGestiune: 'Părintele Producție',
      responsabilId: 'productie1',
      status: 'activ',
      ultimaTranzactie: '2024-01-14',
      tranzactiiLuna: 25,
      venituriLuna: 12000,
      cheltuieliLuna: 15000,
      tip: 'episcopie'
    },
    {
      id: 'episcopie_magazin',
      parohieId: 'episcopie',
      nume: 'Magazin Episcopie',
      parohie: 'Episcopia Ortodoxă Română din Italia',
      oras: 'Roma',
      provincia: 'Roma',
      regione: 'Lazio',
      contBancar: 'IT89M0300203280100000009999',
      banca: 'Intesa Sanpaolo',
      soldInitial: 30000,
      soldCurent: 42000,
      responsabilGestiune: 'Părintele Magazin',
      responsabilId: 'magazin1',
      status: 'activ',
      ultimaTranzactie: '2024-01-15',
      tranzactiiLuna: 60,
      venituriLuna: 25000,
      cheltuieliLuna: 12000,
      tip: 'episcopie'
    },
    // Gestiuni Parohii
    {
      id: 'gest1',
      parohieId: 'parohia1',
      nume: 'Gestiunea Parohiei Sf. Nicolae',
      parohie: 'Parohia Sf. Nicolae',
      oras: 'Porretta Terme',
      provincia: 'Bologna',
      regione: 'Emilia-Romagna',
      contBancar: 'IT60X0542811101000000123456',
      banca: 'Banca Popolare di Sondrio',
      soldInitial: 5000,
      soldCurent: 8500,
      responsabilGestiune: 'Anna Rossi',
      responsabilId: 'user7',
      status: 'activ',
      ultimaTranzactie: '2024-01-15',
      tranzactiiLuna: 12,
      venituriLuna: 3200,
      cheltuieliLuna: 1800,
      tip: 'parohie'
    },
    {
      id: 'gest2',
      parohieId: 'parohia2',
      nume: 'Gestiunea Parohiei Sf. Gheorghe',
      parohie: 'Parohia Sf. Gheorghe',
      oras: 'Milano',
      provincia: 'Milano',
      regione: 'Lombardia',
      contBancar: 'IT02L0300203280100000001234',
      banca: 'UniCredit',
      soldInitial: 15000,
      soldCurent: 22500,
      responsabilGestiune: 'Maria Bianchi',
      responsabilId: 'user8',
      status: 'activ',
      ultimaTranzactie: '2024-01-14',
      tranzactiiLuna: 28,
      venituriLuna: 8500,
      cheltuieliLuna: 4200,
      tip: 'parohie'
    },
    {
      id: 'gest3',
      parohieId: 'parohia3',
      nume: 'Gestiunea Parohiei Sf. Dumitru',
      parohie: 'Parohia Sf. Dumitru',
      oras: 'Roma',
      provincia: 'Roma',
      regione: 'Lazio',
      contBancar: 'IT89M0300203280100000005678',
      banca: 'Intesa Sanpaolo',
      soldInitial: 8000,
      soldCurent: 12000,
      responsabilGestiune: 'Giuseppe Verdi',
      responsabilId: 'user9',
      status: 'activ',
      ultimaTranzactie: '2024-01-13',
      tranzactiiLuna: 18,
      venituriLuna: 4500,
      cheltuieliLuna: 2800,
      tip: 'parohie'
    },
    {
      id: 'gest4',
      parohieId: 'parohia4',
      nume: 'Gestiunea Parohiei Sf. Maria',
      parohie: 'Parohia Sf. Maria',
      oras: 'Torino',
      provincia: 'Torino',
      regione: 'Piemonte',
      contBancar: 'IT75A0300203280100000009012',
      banca: 'Banca Sella',
      soldInitial: 3000,
      soldCurent: 4200,
      responsabilGestiune: 'Francesco Rossi',
      responsabilId: 'user10',
      status: 'activ',
      ultimaTranzactie: '2024-01-12',
      tranzactiiLuna: 8,
      venituriLuna: 1800,
      cheltuieliLuna: 1200,
      tip: 'parohie'
    }
  ];

  const regiuni = Array.from(new Set(gestiuni.map(g => g.regione))).sort();
  const statusuri = ['activ', 'inactiv', 'suspendat'];

  // Date pentru bonuri de transfer și facturi
  const bonuriTransfer: BonTransfer[] = [
    {
      id: 'bt1',
      numar: 'BT-001/2024',
      data: '2024-01-15',
      deLa: 'Producție Episcopie',
      catre: 'Depozit Episcopie',
      produse: [
        {
          id: 'p1',
          nume: 'Lumanari',
          cantitate: 500,
          unitate: 'buc',
          pretUnitar: 2.50,
          valoareTotala: 1250,
          categorie: 'Produse religioase'
        }
      ],
      totalValoare: 1250,
      status: 'confirmat',
      observatii: 'Transfer lumanari produse pentru distribuție'
    },
    {
      id: 'bt2',
      numar: 'BT-002/2024',
      data: '2024-01-14',
      deLa: 'Depozit Episcopie',
      catre: 'Parohia Sf. Nicolae',
      produse: [
        {
          id: 'p2',
          nume: 'Icoane',
          cantitate: 20,
          unitate: 'buc',
          pretUnitar: 15.00,
          valoareTotala: 300,
          categorie: 'Produse religioase'
        },
        {
          id: 'p3',
          nume: 'Cărți religioase',
          cantitate: 50,
          unitate: 'buc',
          pretUnitar: 8.00,
          valoareTotala: 400,
          categorie: 'Literatură religioasă'
        }
      ],
      totalValoare: 700,
      status: 'confirmat',
      observatii: 'Transfer produse pentru parohie'
    },
    {
      id: 'bt3',
      numar: 'BT-003/2024',
      data: '2024-01-13',
      deLa: 'Depozit Episcopie',
      catre: 'Parohia Sf. Gheorghe',
      produse: [
        {
          id: 'p4',
          nume: 'Lumanari',
          cantitate: 200,
          unitate: 'buc',
          pretUnitar: 2.50,
          valoareTotala: 500,
          categorie: 'Produse religioase'
        },
        {
          id: 'p5',
          nume: 'Icoane',
          cantitate: 15,
          unitate: 'buc',
          pretUnitar: 15.00,
          valoareTotala: 225,
          categorie: 'Produse religioase'
        }
      ],
      totalValoare: 725,
      status: 'in_asteptare',
      observatii: 'Transfer produse pentru parohie - în așteptare confirmare'
    }
  ];

  const facturiIntrare: FacturaIntrare[] = [
    {
      id: 'fi1',
      numar: 'FI-001/2024',
      data: '2024-01-10',
      furnizor: 'Atelier Icoane SRL',
      produse: [
        {
          id: 'p6',
          nume: 'Icoane Sf. Nicolae',
          cantitate: 100,
          unitate: 'buc',
          pretUnitar: 12.00,
          valoareTotala: 1200,
          categorie: 'Produse religioase'
        },
        {
          id: 'p7',
          nume: 'Icoane Sf. Maria',
          cantitate: 80,
          unitate: 'buc',
          pretUnitar: 12.00,
          valoareTotala: 960,
          categorie: 'Produse religioase'
        }
      ],
      totalValoare: 2160,
      status: 'platita',
      observatii: 'Achiziție icoane pentru distribuție'
    },
    {
      id: 'fi2',
      numar: 'FI-002/2024',
      data: '2024-01-08',
      furnizor: 'Editura Ortodoxă',
      produse: [
        {
          id: 'p8',
          nume: 'Cărți de rugăciune',
          cantitate: 200,
          unitate: 'buc',
          pretUnitar: 5.00,
          valoareTotala: 1000,
          categorie: 'Literatură religioasă'
        },
        {
          id: 'p9',
          nume: 'Biblia ortodoxă',
          cantitate: 50,
          unitate: 'buc',
          pretUnitar: 15.00,
          valoareTotala: 750,
          categorie: 'Literatură religioasă'
        }
      ],
      totalValoare: 1750,
      status: 'neplatita',
      observatii: 'Achiziție cărți religioase - scadență 30 zile'
    }
  ];

  const filteredGestiuni = gestiuni.filter(gestiune => {
    const matchesSearch = gestiune.nume.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         gestiune.parohie.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         gestiune.oras.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegione = !filterRegione || gestiune.regione === filterRegione;
    const matchesStatus = !filterStatus || gestiune.status === filterStatus;
    
    return matchesSearch && matchesRegione && matchesStatus;
  });

  const handleViewGestiune = (gestiune: GestiuneParohie) => {
    setSelectedGestiune(gestiune);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedGestiune(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'activ': return 'success';
      case 'inactiv': return 'default';
      case 'suspendat': return 'warning';
      default: return 'default';
    }
  };

  const totalSoldCurent = gestiuni.reduce((sum, g) => sum + g.soldCurent, 0);
  const totalVenituriLuna = gestiuni.reduce((sum, g) => sum + g.venituriLuna, 0);
  const totalCheltuieliLuna = gestiuni.reduce((sum, g) => sum + g.cheltuieliLuna, 0);
  const totalTranzactiiLuna = gestiuni.reduce((sum, g) => sum + g.tranzactiiLuna, 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Gestiuni Parohii
        </Typography>
        {hasPermission('gestiuni.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare gestiune nouă */}}
          >
            Gestiune nouă
          </Button>
        )}
      </Box>

      {/* Statistici generale */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountBalanceIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="primary">
                    €{totalSoldCurent.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sold total curent
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ color: 'success.main', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    €{totalVenituriLuna.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Venituri luna curentă
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ color: 'error.main', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="error.main">
                    €{totalCheltuieliLuna.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cheltuieli luna curentă
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InventoryIcon sx={{ color: 'info.main', mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    {totalTranzactiiLuna}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tranzacții luna curentă
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre și căutare */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Caută gestiuni..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Regiune</InputLabel>
              <Select
                value={filterRegione}
                onChange={(e) => setFilterRegione(e.target.value)}
                label="Regiune"
              >
                <MenuItem value="">Toate regiunile</MenuItem>
                {regiuni.map((regione) => (
                  <MenuItem key={regione} value={regione}>
                    {regione}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Toate statusurile</MenuItem>
                {statusuri.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setSearchTerm('');
                setFilterRegione('');
                setFilterStatus('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label={`Toate gestiunile (${filteredGestiuni.length})`} />
          <Tab label={`Episcopie (${filteredGestiuni.filter(g => g.tip === 'episcopie').length})`} />
          <Tab label={`Parohii (${filteredGestiuni.filter(g => g.tip === 'parohie').length})`} />
          <Tab label={`Active (${filteredGestiuni.filter(g => g.status === 'activ').length})`} />
        </Tabs>
      </Box>

      {/* Lista gestiunilor */}
      <Grid container spacing={3}>
        {filteredGestiuni
          .filter(gestiune => {
            if (selectedTab === 0) return true;
            if (selectedTab === 1) return gestiune.tip === 'episcopie';
            if (selectedTab === 2) return gestiune.tip === 'parohie';
            if (selectedTab === 3) return gestiune.status === 'activ';
            return true;
          })
          .map((gestiune) => (
          <Grid item xs={12} md={6} lg={4} key={gestiune.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ChurchIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{gestiune.nume}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {gestiune.parohie}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gestiune.oras}, {gestiune.provincia}
                    </Typography>
                  </Box>
                  <Chip 
                    label={gestiune.status} 
                    color={getStatusColor(gestiune.status) as any}
                    size="small"
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Bancă:</strong> {gestiune.banca}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Cont:</strong> {gestiune.contBancar}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Responsabil:</strong> {gestiune.responsabilGestiune}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Ultima tranzacție:</strong> {new Date(gestiune.ultimaTranzactie).toLocaleDateString('ro-RO')}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="h5" color="primary">
                      €{gestiune.soldCurent.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sold curent
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="success.main">
                      +€{gestiune.venituriLuna.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      -€{gestiune.cheltuieliLuna.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Luna curentă
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto' }}>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewGestiune(gestiune)}
                  >
                    Detalii
                  </Button>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => navigate('/gestiune')}
                  >
                    Gestiune
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog pentru detalii gestiune */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ChurchIcon sx={{ mr: 2, color: 'primary.main' }} />
            {selectedGestiune?.nume}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedGestiune && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Informații generale</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Parohie:</strong> {selectedGestiune.parohie}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Locație:</strong> {selectedGestiune.oras}, {selectedGestiune.provincia}, {selectedGestiune.regione}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Status:</strong> 
                  <Chip 
                    label={selectedGestiune.status} 
                    color={getStatusColor(selectedGestiune.status) as any}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Responsabil:</strong> {selectedGestiune.responsabilGestiune}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Informații bancare</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Bancă:</strong> {selectedGestiune.banca}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Cont bancar:</strong> {selectedGestiune.contBancar}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Sold inițial:</strong> €{selectedGestiune.soldInitial.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Sold curent:</strong> €{selectedGestiune.soldCurent.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Statistici luna curentă</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          €{selectedGestiune.venituriLuna.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Venituri
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main">
                          €{selectedGestiune.cheltuieliLuna.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cheltuieli
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">
                          {selectedGestiune.tranzactiiLuna}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Tranzacții
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Închide
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              handleCloseDialog();
              navigate('/gestiune');
            }}
          >
            Deschide Gestiune
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bonuri de transfer și facturi */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <InventoryIcon sx={{ mr: 1 }} />
              Bonuri de Transfer Recente
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {bonuriTransfer.slice(0, 5).map((bon) => (
                <Card key={bon.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Typography variant="subtitle2">{bon.numar}</Typography>
                      <Chip 
                        label={bon.status} 
                        color={bon.status === 'confirmat' ? 'success' : bon.status === 'in_asteptare' ? 'warning' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {bon.deLa} → {bon.catre}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Data: {new Date(bon.data).toLocaleDateString('ro-RO')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Valoare: €{bon.totalValoare.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {bon.produse.length} produse
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
            <Button 
              variant="outlined" 
              fullWidth 
              sx={{ mt: 2 }}
              onClick={() => navigate('/gestiune')}
            >
              Vezi toate bonurile
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <AccountBalanceIcon sx={{ mr: 1 }} />
              Facturi de Intrare Recente
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {facturiIntrare.slice(0, 5).map((factura) => (
                <Card key={factura.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Typography variant="subtitle2">{factura.numar}</Typography>
                      <Chip 
                        label={factura.status} 
                        color={factura.status === 'platita' ? 'success' : factura.status === 'neplatita' ? 'error' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Furnizor: {factura.furnizor}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Data: {new Date(factura.data).toLocaleDateString('ro-RO')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Valoare: €{factura.totalValoare.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {factura.produse.length} produse
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
            <Button 
              variant="outlined" 
              fullWidth 
              sx={{ mt: 2 }}
              onClick={() => navigate('/gestiune')}
            >
              Vezi toate facturile
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Acces rapid la Gestiune */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Acces rapid la Gestiune
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pentru gestionarea detaliată a produselor, facturilor și tranzacțiilor, accesați modulul Gestiune.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/gestiune')}
        >
          Deschide Gestiune
        </Button>
      </Paper>
    </Box>
  );
};

export default Gestiuni;

